/**
 * Workspaces whose git history exists ONLY on this device — what a sign-out
 * must not delete (T5 of "Track on this device", 04.10.2026).
 *
 * A local-only link is `workspace.git = { provider: 'local', branch, since }`:
 * no remote, no account, no credential. Its every saved version lives in
 * `<account>/workspace-mirror/<workspace id>/.git` and nowhere else, so:
 *
 *   - Sign out asks (signOutStore) and names how many histories are at stake;
 *   - "Keep on this device" passes their ids to the purge, which keeps those
 *     mirror folders (`account_data_purge` `keepMirrorIds`, sync/signout_mirrors.rs);
 *   - the link itself survives clearLinkedAccounts (accountsStore) and the
 *     local workspace manifest (outside the account root), so signing back in
 *     finds the history connected again;
 *   - "Remove and sign out" deletes the histories AND their links
 *     (forgetLocalHistoryLinks, 05.10.2026).
 *
 * T10: "My projects, boards & documents" tracked on this device only keeps its
 * history in `<account>/scope-mirror/personal/.git` — collected by
 * localHistoryScopeFolders and kept the same way (`keepScopeMirrors`).
 *
 * 05.10.2026 (architect review): the lists also read the repositories on DISK
 * (`account_local_histories`). The workspace list in memory can be empty right
 * after a sign-in or partial offline; the dialog then did not open. Rust now
 * keeps every git repository unless "Remove" is chosen, and these lists only
 * decide whether to ASK — a repository on disk whose link is not known to be
 * remote counts as a history kept only on this device.
 */
import { useWorkspaceStore } from '../../store/workspaceStore';
import { getAllWorkspaceGitLinks, getAllScopeGitLinks, setWorkspaceGitLink, setScopeGitLink } from '../accountsStore';
import { listLocalWorkspaceMetasFromManifest, saveLocalWorkspaceManifest } from '../localWorkspaceStore';
import { PERSONAL_LOCAL_SCOPE } from '../personalScope';
import { useAuthStore, outgoingAccountId } from '../../store/authStore';
import { isTauri } from '../../utils/tauri';

/**
 * True for a git link that has no remote. Kept local to this module so this
 * branch and "Track on this device" (which adds `isLocalOnly` to
 * gitSyncStatus.js) do not conflict; switch to `isLocalOnly` once both merge.
 */
export const isLocalOnlyGitLink = (git) => git?.provider === 'local';

/** A link that points at a server: its repository is a copy of the remote. */
const isRemoteGitLink = (git) => Boolean(git) && !isLocalOnlyGitLink(git);

/** The account the sign-out will purge — the same id the purge uses (authStore). */
const outgoingId = () => outgoingAccountId(useAuthStore.getState().user);

/**
 * `{ workspaces, scopes }`: the folder names of the git repositories in this
 * account's mirror folders, read from disk. Empty outside the desktop app or
 * on any error (then the in-memory lists alone decide, as before).
 */
async function repositoriesOnDisk() {
  const id = outgoingId();
  if (!id || !isTauri()) return { workspaces: [], scopes: [] };
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const r = await invoke('account_local_histories', { userId: id });
    return {
      workspaces: Array.isArray(r?.workspaces) ? r.workspaces.map(String) : [],
      scopes: Array.isArray(r?.scopes) ? r.scopes.map(String) : [],
    };
  } catch {
    return { workspaces: [], scopes: [] };
  }
}

/**
 * Ids of the signed-in account's workspaces tracked on this device only.
 *
 * The workspace list is the account's own (read BEFORE the sign-out resets
 * it). A cloud workspace's link may live only in the accounts store
 * (useGitLinksHydration copies it onto the workspace once), so both are read;
 * a stored link counts only for a workspace in this account's list — the
 * accounts store is device-wide.
 *
 * @returns {Promise<string[]>}
 */
export async function localHistoryWorkspaceIds() {
  const workspaces = useWorkspaceStore.getState().workspaces;
  const list = Array.isArray(workspaces) ? workspaces : [];
  let links = {};
  try {
    links = (await getAllWorkspaceGitLinks()) || {};
  } catch {
    links = {};
  }
  const ids = new Set();
  const byId = new Map();
  for (const ws of list) {
    if (ws?.id == null || String(ws.id) === '') continue;
    const id = String(ws.id);
    byId.set(id, ws);
    if (isLocalOnlyGitLink(ws.git) || (!ws.git && isLocalOnlyGitLink(links[id]))) ids.add(id);
  }
  // From disk: a repository whose workspace is not loaded (yet), or whose link
  // is local or unknown. Only a link known to be remote is left out.
  for (const id of (await repositoriesOnDisk()).workspaces) {
    const git = byId.get(id)?.git || links[id];
    if (!isRemoteGitLink(git)) ids.add(id);
  }
  return [...ids];
}

/** The scope-mirror folder of the signed-in account's personal scope (`scopeRepo.scopeFolder`). */
const PERSONAL_FOLDER = 'personal';

/**
 * The `scope-mirror/<folder>` names of this account's scopes tracked on this
 * device only — at most the personal one (team scopes are never local). Read
 * BEFORE the sign-out, while the personal scope is still this account's.
 *
 * @returns {Promise<string[]>}
 */
export async function localHistoryScopeFolders() {
  // The account the purge targets, even while `user` is not loaded (a 401, a
  // damaged auth-storage): personalScopeId() would say nothing then.
  const id = outgoingId();
  const key = id ? `personal:${id}` : null;
  // Signed out ("personal:local") has no account root to purge.
  if (!key || key === PERSONAL_LOCAL_SCOPE) return [];
  let links = {};
  try {
    links = (await getAllScopeGitLinks()) || {};
  } catch {
    links = {};
  }
  if (isLocalOnlyGitLink(links[key])) return [PERSONAL_FOLDER];
  if (isRemoteGitLink(links[key])) return [];
  return (await repositoriesOnDisk()).scopes.includes(PERSONAL_FOLDER) ? [PERSONAL_FOLDER] : [];
}

/**
 * "Remove and sign out" (owner decision 05.10.2026): the purge deletes these
 * histories, so their links go too. Before, the link survived and signing back
 * in showed "History missing on this device" for a history the user had just
 * chosen to delete. Same as "Stop tracking": a removed history leaves nothing
 * pointing at it (browsers' "delete this profile's data" leaves no dangling
 * entries either). Remote links are untouched — their history is on the server.
 *
 * Runs BEFORE the sign-out, while the namespace and the personal scope are
 * still this account's. Never throws: the sign-out must go on regardless.
 *
 * @param {string[]} workspaceIds  from localHistoryWorkspaceIds()
 * @param {string[]} scopeFolders  from localHistoryScopeFolders()
 */
export async function forgetLocalHistoryLinks(workspaceIds = [], scopeFolders = []) {
  const ids = new Set((workspaceIds || []).map(String));
  try {
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      await setWorkspaceGitLink(id, null);
    }
    if (ids.size > 0) {
      const metas = await listLocalWorkspaceMetasFromManifest();
      const next = metas.map((m) => (ids.has(String(m?.id)) && isLocalOnlyGitLink(m.git) ? { ...m, git: undefined } : m));
      if (next.some((m, i) => m !== metas[i])) await saveLocalWorkspaceManifest(next);
    }
    if ((scopeFolders || []).includes(PERSONAL_FOLDER)) {
      const key = outgoingId() ? `personal:${outgoingId()}` : null;
      if (key && key !== PERSONAL_LOCAL_SCOPE) await setScopeGitLink(key, null);
    }
  } catch {
    // A link left behind shows "History missing" — the old behaviour, not a loss.
  }
}
